import React, { useState, useEffect } from 'react';
import EmailCapture from './EmailCapture';
import ShareButtons from './ShareButtons';
import { useT } from '../i18n';

interface RoundResult {
  correct: boolean;
  humanSource: string;
  aiModel: string;
}

interface ResultsSummaryProps {
  results: RoundResult[];
  sessionId: string;
  onPlayAgain: () => void;
  onShowLeaderboard: () => void;
}

export default function ResultsSummary({
  results,
  sessionId,
  onPlayAgain,
  onShowLeaderboard,
}: ResultsSummaryProps) {
  const [percentile, setPercentile] = useState<number | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const correctCount = results.filter((r) => r.correct).length;
  const total = results.length;

  useEffect(() => {
    fetch(`/api/votes/leaderboard?sessionId=${sessionId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.userStats) {
          setPercentile(data.userStats.percentile);
        }
      })
      .catch(console.error);
  }, [sessionId]);

  useEffect(() => {
    const timer = setTimeout(() => setShowDetails(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const t = useT();
  const aiCount = total - correctCount;
  const humanPct = total > 0 ? Math.round((correctCount / total) * 100) : 0;
  const aiPct = 100 - humanPct;

  const getMessage = () => {
    if (aiCount >= 4) return t('results.msg4ai');
    if (aiCount >= 3) return t('results.msg3ai');
    if (aiCount >= 2) return t('results.msg2ai');
    return t('results.msgDefault');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Score Hero */}
        <div className="text-center mb-10">
          <p className="font-display text-lg text-stone-500 italic mb-8">{t('results.title')}</p>

          {/* Big score */}
          <div className="flex items-end justify-center gap-5 mb-3">
            <div className="text-center">
              <div className="font-display text-7xl font-bold text-emerald-600 leading-none">
                {correctCount}
              </div>
              <div className="text-[11px] text-emerald-600/60 font-sans font-medium mt-2 uppercase tracking-widest">
                {t('results.human')}
              </div>
            </div>
            <div className="font-display text-4xl text-stone-300 font-light pb-3">:</div>
            <div className="text-center">
              <div className="font-display text-7xl font-bold text-violet-600 leading-none">
                {aiCount}
              </div>
              <div className="text-[11px] text-violet-600/60 font-sans font-medium mt-2 uppercase tracking-widest">
                {t('results.ai')}
              </div>
            </div>
          </div>

          {/* Proportion bar */}
          <div className="w-40 h-1 mx-auto rounded-full overflow-hidden flex mt-5 mb-8 bg-stone-200">
            <div
              className="bg-emerald-500 h-full transition-all duration-700"
              style={{ width: `${humanPct}%` }}
            />
            <div
              className="bg-violet-500 h-full transition-all duration-700"
              style={{ width: `${aiPct}%` }}
            />
          </div>

          <p className="font-serif text-stone-600 text-base leading-relaxed max-w-xs mx-auto">
            {getMessage()}
          </p>
        </div>

        {/* Details — fade in after 2s */}
        <div
          className={`transition-all duration-1000 ${showDetails ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
        >
          {/* Round dots */}
          <div className="flex justify-center gap-3 mb-10">
            {results.map((r, i) => (
              <div key={i} className="group relative">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-sans font-bold ${
                    r.correct
                      ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200'
                      : 'bg-violet-100 text-violet-700 ring-1 ring-violet-200'
                  }`}
                >
                  {r.correct ? t('results.human').charAt(0) : t('results.ai')}
                </div>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-white border border-stone-200 rounded shadow-sm text-[10px] text-stone-500 font-sans whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  {r.correct ? r.humanSource : r.aiModel}
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mb-4">
            <button
              onClick={onPlayAgain}
              className="flex-1 py-3.5 border border-stone-300 hover:border-stone-500 text-stone-600 hover:text-stone-800 font-display font-semibold rounded-lg transition-all duration-200 text-sm"
            >
              {t('results.playAgain')}
            </button>
            <button
              onClick={onShowLeaderboard}
              className="flex-1 py-3.5 border border-stone-300 hover:border-stone-500 text-stone-500 hover:text-stone-700 font-display rounded-lg transition-all duration-200 text-sm"
            >
              Leaderboard
            </button>
          </div>

          {/* Share */}
          <div className="mb-10">
            <ShareButtons humanCount={correctCount} aiCount={aiCount} total={total} />
          </div>

          <div className="border-t border-stone-200 mb-8" />

          {/* Email */}
          <div className="mb-8 max-w-sm mx-auto">
            <p className="text-xs text-stone-500 font-sans mb-2 text-center">
              {t('results.emailHint')}
            </p>
            <EmailCapture sessionId={sessionId} />
          </div>

          {/* Hermes CTA */}
          <div className="relative overflow-hidden rounded-2xl bg-stone-100 border border-stone-200 p-6">
            <div className="flex flex-col items-center text-center gap-4">
              <img
                src="/hermes_3000_logo.jpg"
                alt="Hermes 3000"
                className="h-20 rounded-xl shadow-md shadow-stone-300"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <p
                className="font-serif text-sm text-stone-600 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t('results.hermesCta') }}
              />
              <a
                href="https://hermes3000.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-stone-800 hover:bg-stone-900 text-white text-sm font-display font-semibold rounded-lg transition-all duration-200 shadow-md shadow-stone-300"
              >
                {t('results.hermesButton')}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="translate-y-0.5"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>

          <p className="text-center text-[11px] text-stone-300 mt-6 font-sans">
            {t('landing.experiment')}{' '}
            <a
              href="https://hermes3000.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-stone-500 transition-colors"
            >
              Hermes 3000
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
